import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BookOpenIcon, FilmIcon, LockIcon, MenuIcon, PauseIcon, PlayIcon } from 'lucide-react';
import { abbVieProNavigation } from '../data/abbviePro';
type Chapter = {
  number: string;
  label: string;
  title: string;
  caption: string;
};
type AbbVieHeroPlayerProps = {
  chapters: readonly Chapter[];
  gated?: boolean;
  exampleLabel?: string;
  className?: string;
  videoSrc?: string;
  videoStartTime?: number;
};
const ADVANCE_MS = 4200;
const TICK_MS = 100;
export function AbbVieHeroPlayer({
  chapters,
  gated = false,
  exampleLabel = 'SKYRIZI · illustrative example',
  className = '',
  videoSrc = 'https://cdn.magicpatterns.com/uploads/7WNoB7Kngo2ANCNqy5nESY/video-1787566550880.mp4',
  videoStartTime = 0
}: AbbVieHeroPlayerProps) {
  const reduceMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mode, setMode] = useState<'player' | 'text'>('player');
  const [locked, setLocked] = useState(gated);
  const active = chapters[activeIndex];
  useEffect(() => {
    if (!isPlaying || mode !== 'player' || locked || reduceMotion) return;
    const interval = setInterval(() => {
      setProgress(previous => {
        const next = previous + TICK_MS / ADVANCE_MS * 100;
        if (next >= 100) {
          setActiveIndex(index => (index + 1) % chapters.length);
          return 0;
        }
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [chapters.length, isPlaying, locked, mode, reduceMotion]);
  const handleTogglePlay = () => {
    if (locked) return;
    if (reduceMotion) {
      setActiveIndex(index => (index + 1) % chapters.length);
      setProgress(0);
      return;
    }
    setIsPlaying(playing => !playing);
  };
  const handleSelectChapter = (index: number) => {
    if (locked) return;
    setActiveIndex(index);
    setProgress(0);
  };
  return <div className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl ${className}`} aria-label="AbbVie Pro website hero embed — illustrative example" data-id="element-60" data-mp="xqh8ey4rzx0k">
      <div className="flex h-9 items-center gap-2 border-b border-slate-200 bg-slate-100 px-4" aria-hidden="true" data-id="element-61" data-mp="oqc1ep3hv0mx">
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" data-id="element-62" data-mp="jskcm3y5bumy" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" data-id="element-63" data-mp="eur4h5da8qk1" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" data-id="element-64" data-mp="a394ajj32u04" />
        <div className="ml-2 flex h-5 max-w-xs flex-1 items-center rounded bg-white px-3 text-[9px] text-slate-400" data-id="element-65" data-mp="a1pkiiy7y0tl">abbviepro.com/products/skyrizi</div>
      </div>

      <header data-id="element-66" data-mp="13dp83meqeut">
        <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-6" data-id="element-67" data-mp="13vt9mfb9xda">
          <div className="flex items-center gap-2 text-abbvie-navy" data-id="element-68" data-mp="3s7khp4ikpsfj">
            <span className="text-lg font-semibold tracking-tight sm:text-xl" data-id="element-69" data-mp="3n9ss4h5eiiei">abbvie</span>
            <span className="rounded bg-abbvie-pro px-1.5 py-0.5 text-[9px] font-bold text-white" data-id="element-70" data-mp="3d5m0b5teoydr">pro</span>
          </div>
          <div className="hidden items-center gap-3 text-[8px] font-semibold text-abbvie-navy sm:flex" data-id="element-71" data-mp="3i3w05tq2sn5p">
            <span data-id="element-72" data-mp="3n3i8ppuylf86">IMMUNOLOGY:</span>
            <span data-id="element-73" data-mp="3n4jlpzvjcbsj">DERMATOLOGY</span>
            <span className="rounded-full bg-abbvie-cyan px-3 py-1.5 text-abbvie-navy" data-id="element-74" data-mp="3s2l7iskcip7p">GASTROENTEROLOGY</span>
          </div>
          <MenuIcon className="h-4 w-4 text-abbvie-navy sm:hidden" aria-hidden="true" data-id="element-75" data-mp="2ufw0236vjkeg" />
        </div>
        <div className="border-y border-slate-200 px-4 sm:px-6" data-id="element-76" data-mp="2zf8v3b7wfxs4">
          <div className="flex gap-4 overflow-hidden py-2 text-[8px] font-semibold text-abbvie-navy sm:gap-6" data-id="element-77" data-mp="34daanm649eyu">
            {abbVieProNavigation.slice(0, 5).map((item, index) => <span key={item} className={index === 0 ? 'border-b-2 border-abbvie-cyan pb-1' : 'whitespace-nowrap'} data-id="element-78" data-mp="395trcs03oq7v" data-mpi={index}>{item}</span>)}
          </div>
        </div>
      </header>

      <div className="bg-sky-50 p-3 sm:p-5" data-id="element-79" data-mp="2glv41h7b4bhs">
        <div className="relative overflow-hidden rounded-xl bg-abbvie-navy text-white shadow-xl" data-id="element-80" data-mp="m6y69hk2wqmi">
          <video src={videoSrc} autoPlay loop muted playsInline className="absolute inset-0 h-full w-full object-cover opacity-40 mix-blend-screen" aria-hidden="true" onLoadedMetadata={e => {
          if (videoStartTime > 0) {
            e.currentTarget.currentTime = videoStartTime;
          }
        }} data-id="element-81" data-mp="h8wlms708j8x" />
          <div className="absolute inset-0 bg-abbvie-navy/60" aria-hidden="true" data-id="element-82" data-mp="10vuaf3j0ljhu" />
          <div className="relative flex items-center justify-between gap-3 border-b border-white/10 p-4 sm:px-6 sm:py-4" data-id="element-83" data-mp="1ev3s67mvclnx">
            <span className="rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white/80" data-id="element-84" data-mp="19xli2dx480ec">{exampleLabel}</span>
            <div className="flex items-center gap-1 rounded-full bg-white/10 p-1" role="group" aria-label="Viewing mode" data-id="element-85" data-mp="3boorx0z6laxu">
              <button type="button" onClick={() => setMode('player')} aria-pressed={mode === 'player'} className={`flex min-h-11 items-center gap-1.5 rounded-full px-3 text-[10px] font-bold transition-colors duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] focus:outline-none focus:ring-2 focus:ring-abbvie-cyan ${mode === 'player' ? 'bg-abbvie-cyan text-abbvie-navy' : 'text-white/60 hover:text-white'}`} data-id="element-86" data-mp="1r64jjn0gthri">
                <FilmIcon className="h-3 w-3" aria-hidden="true" data-id="element-87" data-mp="1dfixxqjmmtpx" /> Watch
              </button>
              <button type="button" onClick={() => setMode('text')} aria-pressed={mode === 'text'} className={`flex min-h-11 items-center gap-1.5 rounded-full px-3 text-[10px] font-bold transition-colors duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] focus:outline-none focus:ring-2 focus:ring-abbvie-cyan ${mode === 'text' ? 'bg-abbvie-cyan text-abbvie-navy' : 'text-white/60 hover:text-white'}`} data-id="element-88" data-mp="yi8m2g32nlto">
                <BookOpenIcon className="h-3 w-3" aria-hidden="true" data-id="element-89" data-mp="2reknk45b1lwj" /> Read
              </button>
            </div>
          </div>

          {mode === 'player' ? <div className="relative flex min-h-[270px] flex-col justify-between px-5 pb-6 pt-5 sm:min-h-[330px] sm:px-7 sm:pb-7" data-id="element-90" data-mp="2mfrld7vgxhbo">
              <div className="flex items-center justify-between gap-4" data-id="element-91" data-mp="3f45jdnmk4dn8">
                <p className="text-xs font-bold text-abbvie-cyan" data-id="element-92" data-mp="3f3dlu79eg5ub">{active.label} <span className="text-white/40" data-id="element-93" data-mp="3a63p06jokrwg">/ {chapters.length}</span></p>
                <span className="text-5xl font-black leading-none text-white/10" aria-hidden="true" data-id="element-94" data-mp="35du3r3tigg0i">{active.number}</span>
              </div>

              <AnimatePresence mode="wait" data-id="element-95" data-mp="31c8udsg6qjv2">
                <motion.div key={activeIndex} initial={reduceMotion ? undefined : {
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={reduceMotion ? undefined : {
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} className="my-5 border-l-2 border-abbvie-cyan pl-5" data-id="element-96" data-mp="1p0jds0h5ikw">
                  <h3 className="max-w-md text-2xl font-bold leading-tight sm:text-4xl" data-id="element-97" data-mp="1okxbwjjr9tb6">{active.title}</h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/60" data-id="element-98" data-mp="1tm4n6sl3xaml">{active.caption}</p>
                </motion.div>
              </AnimatePresence>

              <div data-id="element-99" data-mp="23iziz3pzwwx6">
                <div className="flex items-center gap-4" data-id="element-100" data-mp="23ifxjd14zqee">
                  <motion.button type="button" onClick={handleTogglePlay} whileTap={{
                scale: 0.94
              }} transition={{
                duration: 0.1
              }} disabled={locked} aria-label={reduceMotion ? 'Advance to next episode' : isPlaying ? 'Pause episode' : 'Play episode'} className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-abbvie-navy shadow-lg focus:outline-none focus:ring-2 focus:ring-abbvie-cyan focus:ring-offset-2 focus:ring-offset-abbvie-navy disabled:opacity-40" data-id="element-101" data-mp="23gwi1onb1jdo">
                    {reduceMotion ? <PlayIcon className="ml-0.5 h-5 w-5" aria-hidden="true" data-id="element-102" data-mp="266q6y8ott9d2" /> : isPlaying ? <PauseIcon className="h-5 w-5" aria-hidden="true" data-id="element-103" data-mp="225eqotrue0hj" /> : <PlayIcon className="ml-0.5 h-5 w-5" aria-hidden="true" fill="currentColor" data-id="element-104" data-mp="223tukzpvq9hk" />}
                  </motion.button>

                  {!reduceMotion && <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/15" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)} aria-label={`${active.label} progress`} data-id="element-105" data-mp="19ik9q6u8hj8t">
                      <div className="h-full origin-left rounded-full bg-abbvie-cyan" style={{
                  transform: `scaleX(${progress / 100})`,
                  transition: isPlaying ? 'transform 100ms linear' : 'transform 150ms ease-out'
                }} data-id="element-106" data-mp="1ifujfnrel0bm" />
                    </div>}
                </div>

                <div className="mt-4 flex flex-wrap gap-1.5" role="group" aria-label="Select an episode" data-id="element-107" data-mp="gl9kr05tb48p">
                  {chapters.map((chapter, index) => <button key={chapter.number} type="button" onClick={() => handleSelectChapter(index)} aria-current={index === activeIndex ? 'true' : undefined} aria-label={`Open ${chapter.label}: ${chapter.title}`} disabled={locked} className={`flex h-11 w-11 items-center justify-center rounded-full text-[10px] font-bold transition-colors duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] focus:outline-none focus:ring-2 focus:ring-abbvie-cyan disabled:opacity-40 ${index === activeIndex ? 'bg-abbvie-cyan text-abbvie-navy' : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'}`} data-id="element-108" data-mp="6pg352d8kkk2" data-mpi={index}>
                      {chapter.number}
                    </button>)}
                </div>
              </div>

              {locked && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-abbvie-navy/95 p-8 text-center backdrop-blur-sm" data-id="element-109" data-mp="rccw1vrf1wen">
                  <LockIcon className="h-6 w-6 text-abbvie-cyan" aria-hidden="true" data-id="element-110" data-mp="hmtaujg8dqgm" />
                  <p className="text-sm font-bold" data-id="element-111" data-mp="3ntoxjk3rpo5">Sign in to AbbVie Pro to continue</p>
                  <p className="max-w-xs text-xs text-white/60" data-id="element-112" data-mp="8mn2td3axiou">Required in markets like Finland and Denmark before episodes unlock.</p>
                  <button type="button" onClick={() => setLocked(false)} className="mt-2 min-h-11 rounded-full bg-abbvie-cyan px-4 text-xs font-bold text-abbvie-navy transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-white" data-id="element-113" data-mp="3pvjqjihoqas3">Preview episode 1</button>
                </div>}
            </div> : <ol className="relative space-y-0 px-5 pb-6 sm:px-7 sm:pb-8" data-id="element-114" data-mp="3dlkg2o7scve5">
              {chapters.map(chapter => <li key={chapter.number} className="flex gap-4 border-t border-white/10 py-4 first:border-t-0" data-id="element-115" data-mp="38n0reg1t55i3">
                  <span className="text-xs font-bold text-abbvie-cyan" data-id="element-116" data-mp="3nbn6qhrfpx9b">{chapter.number}</span>
                  <div data-id="element-117" data-mp="3iiatf3s7hcvv"><p className="text-sm font-bold" data-id="element-118" data-mp="3iiaul73xzixg">{chapter.title}</p><p className="mt-1 text-xs leading-relaxed text-white/55" data-id="element-119" data-mp="535tfxucoz6">{chapter.caption}</p></div>
                </li>)}
            </ol>}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-slate-200 px-4 py-3 text-[9px] font-semibold text-slate-400 sm:px-6" data-id="element-120" data-mp="a4bze7oso1l0">
        <span data-id="element-121" data-mp="55ikcatv02iq">Embedded inside the existing AbbVie Pro page</span>
        <span className="text-abbvie-cyan" data-id="element-122" data-mp="2budq02zoxjbp">Continue browsing below ↓</span>
      </div>
    </div>;
}